import Highcharts from 'highcharts';
import { GrowthTypes } from '@components/comparison-growth-selector/Types';
import { renderLoading } from '@pages/Loading';
import { NoResult } from '@pages/NoResult';
import { isKpiShare } from '@utils/TableUtils';
import { RetailerKpiOption, SaleType } from '@models/Products';
import { SortingOptionsType } from '@components/design/migrated/Table';
import { ColumnHeaderForCL } from '@models/CategoryLeaderboard';
import { ShareType } from '@utils/pageConfig/Types';
import { StackedChartResp } from '../stacked-chart/Types';
import ErrorResultMessage from '@pages/ErrorResultMessage';

export const resizeChart = () => {
  setTimeout(() => {
    for (let i = 0; i < Highcharts.charts.length; i++) {
      Highcharts?.charts[i]?.reflow();
    }
  }, 500);
};

export const displayChangeType = (displayGrowthTypes: GrowthTypes, kpiKey: any) => {
  return displayGrowthTypes === GrowthTypes.percentage && !isKpiShare(kpiKey) ? '%' : '';
};

interface RenderVisualizationProps {
  isLoading: boolean;
  fetchedApiData: any;
  defaultData: any;
  jsxToRender: JSX.Element;
  noResultHandler?: () => void;
  noResultJsx?: JSX.Element;
  noResultPredicate?: () => boolean;
  noPeriodExistForMulticountry?: boolean;
  showLoader?: boolean;
  errorMessageJsx?: JSX.Element;
  isError?: boolean;
}

type RenderVisualizationDeferProps = Omit<
  RenderVisualizationProps,
  'jsxToRender' | 'noResultJsx' | 'errorMessageJsx'
> & {
  jsxToRenderDefer: () => JSX.Element;
  noResultJsxDefer?: () => JSX.Element;
  timeoutJsxDefer?: () => JSX.Element;
};

/**
 * Performs rendering of visualization (JSX element) according to states, decided by parameters.
 *
 * Designed to be used across all visualizations (graphs / tables etc).
 *
 * Supported states:
 * 1. Loading
 * 2. No results
 * 3. Actual visualization
 *
 * @param {RenderVisualizationProps} props
 * @param {boolean} props.isLoading Determines whether visualization is loading, if it is then render the <Loading /> component.
 * @param {any} props.fetchedApiData Loaded data. Typed as any to cater for different kinds of data.
 * @param {any} props.defaultData Default data. If loaded data is the same as default data, it'll be considered as no result.
 * @param {any} props.jsxToRender JSX.Element to render if there is data.
 * @param {any} props.noResultHandler Function to execute if there is no data.
 * @param {any} props.noResultJsx JSX.Element to render if there is no data.
 * @param {any} props.noResultPredicate check whether the data is present
 * @returns Loading component / No results component / Rendered component
 * @returns
 */
export const renderVisualization = ({
  isLoading,
  fetchedApiData,
  defaultData,
  jsxToRender,
  noResultHandler,
  noResultJsx,
  noResultPredicate,
  noPeriodExistForMulticountry,
  showLoader = true,
  errorMessageJsx,
  isError = false,
}: RenderVisualizationProps): JSX.Element => {
  if (isLoading && showLoader) {
    return renderLoading();
  }
  // Shallow compare data with default data. If data same with defaultData object then consider no results.
  if (
    !isLoading &&
    (!fetchedApiData ||
      JSON.stringify(fetchedApiData) === JSON.stringify(defaultData) ||
      noResultPredicate?.() ||
      noPeriodExistForMulticountry)
  ) {
    if (noResultHandler) {
      noResultHandler();
    }

    if (isError) {
      return errorMessageJsx ?? ErrorResultMessage();
    }

    return noResultJsx ?? NoResult();
  }
  return jsxToRender;
};

/**
 * Similar function as renderVisualization(), however this defers the execution of both the
 * JSX.Element with result and JSX.Element with no result.
 *
 * There are certain cases where the logic of jsxToRender should only be executed if there is data,
 * and when there is no data, the logic throws error. This would solve the issue as it will defer
 * the render execution.
 *
 * @param {RenderVisualizationDeferProps} props
 * @param {boolean} props.isLoading Determines whether visualization is loading, if it is then render the <Loading /> component.
 * @param {any} props.fetchedApiData Loaded data. Typed as any to cater for different kinds of data.
 * @param {any} props.defaultData Default data. If loaded data is the same as default data, it'll be considered as no result.
 * @param {any} props.jsxToRenderDefer Logic to render the JSX.Element if there is data. This is made a function to defer the logic execution.
 * @param {any} props.noResultHandler Function to execute if there is no data.
 * @param {any} props.noResultJsxDefer Logic to render the JSX.Element if there is no data. This is made a function to defer the logic execution.
 * @returns Loading component / No results component / Rendered component
 */
export const renderVisualizationDefer = ({
  isLoading,
  fetchedApiData,
  defaultData,
  jsxToRenderDefer,
  noResultHandler,
  noResultJsxDefer,
}: RenderVisualizationDeferProps): JSX.Element => {
  if (isLoading) {
    return renderLoading();
  }
  // Shallow compare data with default data. If data same with defaultData object then consider no results.
  if (!fetchedApiData || JSON.stringify(fetchedApiData) === JSON.stringify(defaultData)) {
    if (noResultHandler) {
      noResultHandler();
    }
    return noResultJsxDefer?.() ?? NoResult();
  }
  return jsxToRenderDefer();
};

/**
 * gets sale type from sorting options
 * @param {SortingOptionsType} sortingOptions
 * @returns {SaleType} sale type
 */
export const getSaleTypeFromSortingOption = (sortingOptions: SortingOptionsType): SaleType => {
  const { headerKey } = sortingOptions;

  const saleType = headerKey as ColumnHeaderForCL;

  if (
    [ColumnHeaderForCL.marketRevenue, ColumnHeaderForCL.yourRevenue, ColumnHeaderForCL.yourRevenueShare].includes(
      saleType,
    )
  ) {
    return SaleType.revenue;
  }

  if (
    [ColumnHeaderForCL.marketVolume, ColumnHeaderForCL.yourVolume, ColumnHeaderForCL.yourVolumeShare].includes(saleType)
  ) {
    return SaleType.volume;
  }

  if ([ColumnHeaderForCL.marketPrice, ColumnHeaderForCL.yourPrice].includes(saleType)) {
    return SaleType.price;
  }

  return SaleType.revenue;
};

/**
 * gets header key from value in ProductsMarketFilter
 * @param {string} value
 * @returns {string}
 */
export const getHeaderKey = (value: string): string => {
  switch (value) {
    case SaleType.volume:
      return ColumnHeaderForCL.marketVolume;
    case SaleType.price:
      return ColumnHeaderForCL.marketPrice;
    case SaleType.revenue:
    default:
      return ColumnHeaderForCL.marketRevenue;
  }
};

/**
 * transform ShareType to RetailerKpiOption
 * @param shareType - ShareType
 * @returns {RetailerKpiOption}
 */
export const transformShareTypeToRetailerKpiOption = (shareType: ShareType): RetailerKpiOption => {
  switch (shareType) {
    case ShareType.MyMix:
      return RetailerKpiOption.mix;
    case ShareType.Absolute:
      return RetailerKpiOption.absolute;
    case ShareType.MarketShare:
    default:
      return RetailerKpiOption.share;
  }
};

/**
 * transform RetailerKpiOption to ShareType
 * @param {RetailerKpiOption} retailerKpiOption
 * @returns {ShareType}
 */
export const transformRetailerKpiOptionToShareType = (retailerKpiOption: RetailerKpiOption): ShareType => {
  switch (retailerKpiOption) {
    case RetailerKpiOption.mix:
      return ShareType.MyMix;
    case RetailerKpiOption.share:
    default:
      return ShareType.MarketShare;
  }
};

/**
 Function to sort legend data in ascending order by extracting 3 key features:
 1. First element that matches "<="
 2. The last element that matches the regexExp (any non numerical elements)
 3. The remaining elements that don't match the above conditions
 **/
export const sortLegendDataAscending = (legendData: StackedChartResp[]): StackedChartResp[] => {
  if (!legendData.length) return [];
  const { series } = legendData[0].share;
  const regex = /\d/;
  const firstOption = series.filter((x) => x.label.startsWith('<='));
  const lastOption = series.filter((x) => !regex.test(x.label));
  const sortedRemainingOptions = series
    .filter((x) => !firstOption.some((y) => y.label === x.label) && !lastOption.some((y) => y.label === x.label))
    .reduce((acc, item) => ({ ...acc, [item.label]: item }), {});
  const sortedKeys = Object.keys(sortedRemainingOptions).sort(
    (a, b) => parseFloat(a.split('<')[0].replace(',', '')) - parseFloat(b.split('<')[0].replace(',', '')),
  );
  const remainingSortedSeries = sortedKeys.map((key) => sortedRemainingOptions[key]);
  legendData[0].share.series = [...new Set([...firstOption, ...remainingSortedSeries, ...lastOption])];
  return legendData;
};
